import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CreateProjectModal from '../components/project/CreateProject';
import DeleteProjectModal from '../components/project/DeleteProject';
import DetailProjectModal from '../components/project/DetailProject';
import UpdateProjectModal from '../components/project/UpdateProject';

export default function ProjectManagement() {
    const [projects, setProjects] = useState([]);
    const [clients, setClients] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // Filtro de status
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOption, setSortOption] = useState(''); // Opção de ordenação
    const projectsPerPage = 10;

    const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
    const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [isModalUpdateOpen, setIsModalUpdateOpen] = useState(false);

    useEffect(() => {
        consultarCliente();
        fetchProjects();
    }, []);

    const consultarCliente = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/clientRoutes/clientes`);
            if (Array.isArray(response.data)) {
                setClients(response.data);
            } else {
                console.error('Erro: dados de clientes não são um array.');
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/projectRoutes/projects`);
            if (Array.isArray(response.data)) {
                setProjects(response.data);
                filterAndSortProjects(response.data, searchTerm, sortOption, statusFilter); // Aplica o filtro de status quando os projetos são carregados
            } else {
                console.error('Erro: dados de projetos não são um array.');
            }
        } catch (error) {
            console.error('Erro ao buscar projetos:', error);
        }
    };

    const handleUpdateProject = async (updatedProjectData) => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/projectRoutes/projects/${updatedProjectData.id}`, // id do projeto na URL
                {
                    id_cliente: updatedProjectData.id_cliente,
                    valor: updatedProjectData.valor,
                    data_inicio: updatedProjectData.data_inicio,
                    data_termino: updatedProjectData.data_termino,
                    status: updatedProjectData.status,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response && response.data) {
                fetchProjects(); // Atualiza a lista de projetos após update
                setIsModalUpdateOpen(false);
            }
        } catch (error) {
            console.error('Erro ao atualizar projeto:', error);
        }
    };



    const handleSelectProjectForUpdate = (project) => {
        setSelectedProject(project);
        setIsModalUpdateOpen(true);
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        filterAndSortProjects(projects, term, sortOption, statusFilter);
        setCurrentPage(1); // Resetar para a primeira página ao fazer uma nova busca
    };

    const handleSortChange = (e) => {
        const option = e.target.value;
        setSortOption(option);
        filterAndSortProjects(projects, searchTerm, option, statusFilter);
    };

    const handleStatusFilterChange = (e) => {
        const status = e.target.value;
        setStatusFilter(status);
        filterAndSortProjects(projects, searchTerm, sortOption, status);
    };

    const filterAndSortProjects = (projects, searchTerm, sortOption, statusFilter) => {
        let filtered = projects;

        // Filtrar por nome do cliente
        if (searchTerm) {
            filtered = filtered.filter((project) => {
                const cliente = clients.find(client => client.id === project.id_cliente);
                return cliente && cliente.nome.toLowerCase().includes(searchTerm);
            });
        }

        // Filtrar por status do projeto
        if (statusFilter) {
            filtered = filtered.filter((project) => project.status.toLowerCase() === statusFilter.toLowerCase());
        }

        // Ordenar os resultados
        if (sortOption === 'valorCrescente') {
            filtered = filtered.sort((a, b) => a.valor - b.valor);
        } else if (sortOption === 'valorDecrescente') {
            filtered = filtered.sort((a, b) => b.valor - a.valor);
        } else if (sortOption === 'dataInicioCrescente') {
            filtered = filtered.sort((a, b) => new Date(a.data_inicio) - new Date(b.data_inicio));
        } else if (sortOption === 'dataInicioDecrescente') {
            filtered = filtered.sort((a, b) => new Date(b.data_inicio) - new Date(a.data_inicio));
        }

        setFilteredProjects(filtered);
    };

    const handleSelectProject = (project) => {
        setSelectedProject(project);
        setIsModalDetailOpen(true);
    };

    const handleDeleteProject = (project) => {
        setSelectedProject(project);
        setIsModalDeleteOpen(true);
    };

    const handleOpenCreateModal = () => setIsModalCreateOpen(true);
    const handleCloseCreateModal = () => setIsModalCreateOpen(false);
    const handleCloseDetailModal = () => setIsModalDetailOpen(false);
    const handleCloseDeleteModal = () => setIsModalDeleteOpen(false);
    const handleCloseUpdateModal = () => setIsModalUpdateOpen(false);

    const indexOfLastProject = currentPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

    const goToPage = (page) => setCurrentPage(page);

    const formatarParaBRL = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(valor);
    };

    function capitalizeFirstLetter(str) {
        if (typeof str !== 'string' || str.length === 0) {
            return str; // Retorna a string como está se não for válida
        }
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    return (
        <div className="p-4 md:p-8 animate-fade-in max-w-7xl mx-auto">
            <div className="flex flex-col mb-8">
                <h1 className="text-3xl font-serif text-white font-bold tracking-wide">Gerenciamento de Projetos</h1>
                <p className="text-gray-400 mt-2 text-sm">Visualize, crie e administre os projetos em andamento</p>
            </div>

            <div className="glass-panel p-6 mb-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
                        <input
                            type="text"
                            placeholder="Buscar por cliente..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="glass-input w-full md:w-64"
                        />

                        <select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            className="glass-input w-full md:w-48 appearance-none bg-background-darker"
                        >
                            <option value="">Filtrar por Status</option>
                            <option value="Novo">Novo</option>
                            <option value="Em Andamento">Em Andamento</option>
                            <option value="Concluído">Concluído</option>
                            <option value="Aguardando">Aguardando</option>
                        </select>

                        <select
                            value={sortOption}
                            onChange={handleSortChange}
                            className="glass-input w-full md:w-56 appearance-none bg-background-darker"
                        >
                            <option value="">Ordenar</option>
                            <option value="valorCrescente">Valor (Crescente)</option>
                            <option value="valorDecrescente">Valor (Decrescente)</option>
                            <option value="dataInicioCrescente">Data (Crescente)</option>
                            <option value="dataInicioDecrescente">Data (Decrescente)</option>
                        </select>
                    </div>

                    <div className="w-full md:w-auto">
                        <button
                            onClick={handleOpenCreateModal}
                            className="glass-button w-full md:w-auto flex items-center justify-center"
                        >
                            <span className="text-xl mr-2">+</span> Criar Projeto
                        </button>
                    </div>
                </div>
            </div>

            <div className="glass-panel overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="glass-table">
                        <thead>
                        <tr>
                            <th>Cliente</th>
                            <th className="hidden lg:table-cell">Data de Início</th>
                            <th className="hidden lg:table-cell">Data de Entrega</th>
                            <th>Valor</th>
                            <th>Status</th>
                            <th className="text-center">Ações</th>
                        </tr>
                        </thead>
                        <tbody className="text-gray-200 text-sm">
                        {currentProjects.map((project) => {
                            const cliente = clients.find(client => client.id === project.id_cliente);
                            return (
                                <tr key={project.id}>
                                    <td className="font-medium text-white">
                                        {cliente ? cliente.nome : 'Cliente não encontrado'}
                                    </td>
                                    <td className="hidden lg:table-cell">{project.data_inicio}</td>
                                    <td className="hidden lg:table-cell">{project.data_termino}</td>
                                    <td className="font-semibold text-green-400">{formatarParaBRL(project.valor)}</td>
                                    <td>
                                        <span className="bg-white/10 px-3 py-1 rounded-full text-xs border border-white/5">
                                            {capitalizeFirstLetter(project.status)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex justify-center space-x-2">
                                            <button
                                                onClick={() => handleSelectProject(project)}
                                                className="text-blue-400 hover:text-blue-300 p-2 rounded-lg hover:bg-blue-400/10 transition-colors"
                                                title="Detalhes"
                                            >
                                                Detalhes
                                            </button>
                                            <button
                                                onClick={() => handleSelectProjectForUpdate(project)}
                                                className="text-amber-400 hover:text-amber-300 p-2 rounded-lg hover:bg-amber-400/10 transition-colors"
                                                title="Atualizar"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleDeleteProject(project)}
                                                className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-400/10 transition-colors"
                                                title="Excluir"
                                            >
                                                Excluir
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex justify-center items-center mt-8 space-x-2 pb-8">
                {Array.from({ length: totalPages }, (_, index) => (
                    <button
                        key={index}
                        onClick={() => goToPage(index + 1)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                            currentPage === index + 1
                                ? 'bg-primary text-white shadow-lg border border-primary/50'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/5 hover:text-white'
                        }`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>

            {/* Modal de Cadastro */}
            {isModalCreateOpen && <CreateProjectModal onClose={handleCloseCreateModal} fetchProjects={fetchProjects}/>}

            {/* Modal de Detalhe */}
            {isModalDetailOpen && <DetailProjectModal onClose={handleCloseDetailModal} project={selectedProject} clients={clients}/>}

            {/* Modal de Exclusão */}
            {isModalDeleteOpen && <DeleteProjectModal onClose={handleCloseDeleteModal} project={selectedProject} fetchProjects={fetchProjects}/>}

            {/* Modal de Atualização */}
            {isModalUpdateOpen && <UpdateProjectModal project={selectedProject} onClose={handleCloseUpdateModal} onUpdateProject={handleUpdateProject} clients={clients} />}
        </div>
    );
}
